import { useState, useEffect } from 'react'
import { supabase, syncOfflineWrites } from '../services/api'
import { clearAllCache } from '../services/offlineCache'
import { clearOutbox } from '../services/offlineOutbox'
import { clearOpenSession, setSessionOwner } from '../services/sessionStore'
import { arrivedFromRecoveryLink } from '../services/authUrlState'

// ── Password-recovery flag, shared across every useAuth() instance ──────────
// useAuth is called independently by the app shell, AuthView and the reset
// screen; each call has its own React state. `user` stays consistent because
// Supabase broadcasts auth events to all of them — but a flag we set ourselves
// would only ever move in the instance that set it, so the shell would keep
// rendering the reset screen after the reset had been completed. One
// module-level value with a subscriber list keeps every instance honest.
let recoveryActive = arrivedFromRecoveryLink()
const recoveryListeners = new Set()
function setRecoveryActive(next) {
  if (recoveryActive === next) return
  recoveryActive = next
  recoveryListeners.forEach(fn => { try { fn(next) } catch { /* never break auth */ } })
}

export function useAuth() {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  // A password-reset link signs the person in (that session is what authorises
  // the password change) — so without this flag the app would simply drop them
  // on the dashboard, or worse, into onboarding, and never offer the reset they
  // clicked for. Seeded synchronously from the URL so the shell knows on the
  // very first render, then confirmed by Supabase's PASSWORD_RECOVERY event.
  const [passwordRecovery, setPasswordRecoveryState] = useState(recoveryActive)
  const setPasswordRecovery = setRecoveryActive

  useEffect(() => {
    recoveryListeners.add(setPasswordRecoveryState)
    setPasswordRecoveryState(recoveryActive)
    return () => { recoveryListeners.delete(setPasswordRecoveryState) }
  }, [])

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSessionOwner(session?.user?.id ?? null)
      setUser(session?.user ?? null)
      setLoading(false)
      if (session?.user) syncOfflineWrites() // replay anything queued before a reload
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Sign-out wipes the device copy of any unfinished conversation as well
      // as the caches; the server-side history is untouched (pre-beta privacy
      // wave, 4 Sep 2026). It also clears any password-recovery state, so a
      // cancelled reset cannot leave the shell stuck on the reset screen.
      if (event === 'SIGNED_OUT') {
        clearAllCache(); clearOutbox(); clearOpenSession(); setPasswordRecovery(false)
      }
      setSessionOwner(session?.user?.id ?? null)
      if (event === 'SIGNED_IN') syncOfflineWrites()
      if (event === 'PASSWORD_RECOVERY') setPasswordRecovery(true)
      setUser(session?.user ?? null)
    })

    return () => subscription.unsubscribe()
  }, [])

  const signIn = (email, password) =>
    supabase.auth.signInWithPassword({ email, password }).then(({ error }) => {
      if (error) throw error
    })

  // meta: optional user metadata stored on the auth user at signup
  // (e.g. { date_of_birth, age_confirmed_18 } from the 18+ gate). The backend
  // reads it during onboarding and recomputes age server-side — never trusted
  // as-is for entitlement.
  const signUp = (email, password, meta) =>
    supabase.auth.signUp({ email, password, ...(meta ? { options: { data: meta } } : {}) }).then(({ data, error }) => {
      if (error) throw error
      // No session + email confirmation on => the person must confirm via
      // email before they can sign in. Surface that so the UI can say so.
      return { confirmationRequired: !data?.session }
    })

  const signOut = () => supabase.auth.signOut()

  // ── Password recovery ─────────────────────────────────────────────────────
  // Account-enumeration safety is the caller's job as well as ours: this
  // resolves the same way whether or not the address belongs to an account, and
  // the only error it re-throws is rate limiting (which the person needs to see
  // so they stop retrying). Supabase itself does not reveal existence either.
  const requestPasswordReset = (email) =>
    supabase.auth
      .resetPasswordForEmail(String(email || '').trim(), { redirectTo: window.location.origin })
      .then(({ error }) => {
        if (error && /rate limit|too many/i.test(String(error.message || ''))) throw error
        // Everything else is swallowed on purpose — "no such user", "invalid
        // email", provider hiccups. Telling the person which of those happened
        // would tell an attacker which addresses have accounts here.
        return { ok: true }
      })

  // Sets the new password using the session the recovery link established.
  // Deliberately leaves the recovery flag up: the reset screen stays mounted to
  // confirm what happened, and clears it itself when the person moves on.
  const updatePassword = (password) =>
    supabase.auth.updateUser({ password }).then(({ error }) => {
      if (error) throw error
    })

  // "Done — take me into the app." Clears the recovery flag for every instance.
  const finishPasswordRecovery = () => setPasswordRecovery(false)

  // Leaving the reset screen without changing anything (they clicked the link
  // by mistake, or changed their mind). Signing out is deliberate: the recovery
  // session is a means to reset a password, not a normal sign-in, and it should
  // not silently become one.
  const cancelPasswordRecovery = () => {
    setPasswordRecovery(false)
    return supabase.auth.signOut()
  }

  // Re-send the sign-up confirmation email. Same neutral contract as above.
  const resendConfirmation = (email) =>
    supabase.auth
      .resend({ type: 'signup', email: String(email || '').trim() })
      .then(({ error }) => {
        if (error && /rate limit|too many/i.test(String(error.message || ''))) throw error
        return { ok: true }
      })

  return {
    user, loading, signIn, signUp, signOut,
    passwordRecovery, requestPasswordReset, updatePassword,
    finishPasswordRecovery, cancelPasswordRecovery,
    resendConfirmation,
  }
}
